import { useState, useRef } from "react"
import { Link } from "@inertiajs/react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { OptimizedImage } from "@/components/ui/optimized-image"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { Video, Listing } from "@/types"

const ASPECT_RATIOS = [
  { value: "portrait", label: "Portrait (9:16)", desc: "TikTok, Reels, Shorts" },
  { value: "landscape", label: "Landscape (16:9)", desc: "YouTube, Facebook" },
  { value: "square", label: "Square (1:1)", desc: "Instagram feed" },
] as const

const TEMPLATES = [
  { value: "modern", label: "Modern", desc: "Clean lines, bold typography" },
  { value: "luxury", label: "Luxury", desc: "Refined, elegant presentation" },
  { value: "classic", label: "Classic", desc: "Traditional, warm feeling" },
  { value: "bold", label: "Bold", desc: "Eye-catching, high contrast" },
] as const

const TRANSITIONS = [
  { value: "fade", label: "Fade" },
  { value: "slide", label: "Slide" },
  { value: "zoom", label: "Zoom" },
  { value: "cut", label: "Cut" },
] as const

type ImageData = { signed_id: string; url: string; thumb_url: string; description?: string }

type StyleData = {
  name: string
  aspect_ratio: Video["aspect_ratio"]
  template_variant: Video["template_variant"]
  transition_style: Video["transition_style"]
  target_length: number
}

export default function WizardStepStyle({
  data,
  onChange,
  listing,
  selectedImages,
  onImagesChange,
}: {
  data: StyleData
  onChange: (updates: Partial<StyleData>) => void
  listing: Listing
  selectedImages: string[]
  onImagesChange: (images: string[]) => void
}) {
  const allImages: ImageData[] = listing.images_data || []
  const [selected, setSelected] = useState<string[]>(
    selectedImages.length > 0 ? selectedImages : allImages.map((i) => i.signed_id),
  )

  // Drag state
  const dragItemRef = useRef<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)

  function toggle(signedId: string) {
    const next = selected.includes(signedId)
      ? selected.filter((id) => id !== signedId)
      : [...selected, signedId]
    setSelected(next)
    onImagesChange(next)
  }

  function selectAll() {
    const all = allImages.map((i) => i.signed_id)
    setSelected(all)
    onImagesChange(all)
  }

  function deselectAll() {
    setSelected([])
    onImagesChange([])
  }

  // --- Drag handlers ---

  function handleDragStart(e: React.DragEvent, signedId: string) {
    dragItemRef.current = signedId
    e.dataTransfer.effectAllowed = "move"
    const el = e.currentTarget as HTMLElement
    requestAnimationFrame(() => {
      el.style.opacity = "0.4"
    })
  }

  function handleDragEnd(e: React.DragEvent) {
    dragItemRef.current = null
    setDragOverId(null)
    const el = e.currentTarget as HTMLElement
    el.style.opacity = ""
  }

  function handleDragOver(e: React.DragEvent, signedId: string) {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    if (dragItemRef.current && dragItemRef.current !== signedId) {
      setDragOverId(signedId)
    }
  }

  function handleDragLeave() {
    setDragOverId(null)
  }

  function handleDrop(e: React.DragEvent, targetId: string) {
    e.preventDefault()
    setDragOverId(null)
    const sourceId = dragItemRef.current
    if (!sourceId || sourceId === targetId) return

    const sourceIdx = selected.indexOf(sourceId)
    const targetIdx = selected.indexOf(targetId)
    if (sourceIdx === -1 || targetIdx === -1) return

    const next = [...selected]
    next.splice(sourceIdx, 1)
    next.splice(targetIdx, 0, sourceId)
    setSelected(next)
    onImagesChange(next)
  }

  // Build display order: selected first (in order), then unselected
  const orderedImages: ImageData[] = [
    ...selected.map((sid) => allImages.find((i) => i.signed_id === sid)).filter(Boolean) as ImageData[],
    ...allImages.filter((i) => !selected.includes(i.signed_id)),
  ]

  const recommendedCount = data.target_length === 15 ? 4 : 8

  return (
    <div>
      <h2 className="mb-1 font-display text-2xl font-semibold text-charcoal">
        Style &amp; format
      </h2>
      <p className="mb-6 text-warm-grey">
        Choose the look and feel for your video.
      </p>

      <div className="space-y-8">
        {/* Name */}
        <div>
          <Label htmlFor="video-name">Video name</Label>
          <Input
            id="video-name"
            value={data.name}
            onChange={(e) => onChange({ name: e.target.value })}
            className="mt-1.5 max-w-md"
          />
        </div>

        {/* Aspect ratio */}
        <div>
          <Label className="mb-3 block">Aspect ratio</Label>
          <div className="grid gap-3 sm:grid-cols-3">
            {ASPECT_RATIOS.map((ar) => (
              <button
                key={ar.value}
                type="button"
                onClick={() => onChange({ aspect_ratio: ar.value })}
                className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors ${
                  data.aspect_ratio === ar.value
                    ? "border-red bg-red/5"
                    : "border-stone/40 hover:border-stone"
                }`}
              >
                <div
                  className={`flex items-center justify-center rounded border border-current/20 ${
                    ar.value === "portrait"
                      ? "h-12 w-7"
                      : ar.value === "square"
                        ? "size-10"
                        : "h-7 w-12"
                  } ${data.aspect_ratio === ar.value ? "text-red" : "text-warm-grey"}`}
                >
                  <div className="size-1.5 rounded-full bg-current" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-charcoal">{ar.label}</p>
                  <p className="text-[11px] text-warm-grey">{ar.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Template */}
        <div>
          <Label className="mb-3 block">Template</Label>
          <div className="grid gap-3 sm:grid-cols-4">
            {TEMPLATES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => onChange({ template_variant: t.value })}
                className={`rounded-lg border-2 p-3 text-left transition-colors ${
                  data.template_variant === t.value
                    ? "border-red bg-red/5"
                    : "border-stone/40 hover:border-stone"
                }`}
              >
                <p className="text-sm font-medium text-charcoal">{t.label}</p>
                <p className="text-[11px] text-warm-grey">{t.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Transition + Length row */}
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <Label htmlFor="transition">Transition</Label>
            <Select
              value={data.transition_style}
              onValueChange={(v) => onChange({ transition_style: v as Video["transition_style"] })}
            >
              <SelectTrigger className="mt-1.5 w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRANSITIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-3 block">Target length</Label>
            <div className="flex gap-3">
              {[15, 30].map((len) => (
                <button
                  key={len}
                  type="button"
                  onClick={() => onChange({ target_length: len })}
                  className={`rounded-lg border-2 px-6 py-2.5 text-sm font-medium transition-colors ${
                    data.target_length === len
                      ? "border-red bg-red/5 text-red"
                      : "border-stone/40 text-slate hover:border-stone"
                  }`}
                >
                  {len}s
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Photos section */}
        <div className="border-t border-stone/30 pt-8">
          <h3 className="mb-1 font-display text-xl font-semibold text-charcoal">
            Select photos
          </h3>
          <p className="mb-2 text-warm-grey">
            Choose and drag to reorder. The first image is used for the title card.
          </p>
          <p className="mb-6 text-sm text-warm-grey">
            We recommend around {recommendedCount} photos for a {data.target_length}s clip.{" "}
            <Link
              href={`/dashboard/listings/${listing.slug}`}
              className="text-red underline underline-offset-2 hover:text-deep-red"
            >
              Need more? Add them to the listing
            </Link>
          </p>

          {/* Quick actions */}
          <div className="mb-4 flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={selectAll}>
              Select all
            </Button>
            <Button variant="ghost" size="sm" onClick={deselectAll}>
              Deselect all
            </Button>
            <span className="text-xs text-warm-grey">
              {selected.length} of {allImages.length} selected
            </span>
          </div>

          {/* Image grid with drag-and-drop reordering */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {orderedImages.map((img) => {
              const isSelected = selected.includes(img.signed_id)
              const orderIndex = selected.indexOf(img.signed_id)
              const isDragOver = dragOverId === img.signed_id

              return (
                <div key={img.signed_id} className="flex flex-col">
                  <div
                    draggable={isSelected}
                    onDragStart={(e) => handleDragStart(e, img.signed_id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={isSelected ? (e) => handleDragOver(e, img.signed_id) : undefined}
                    onDragLeave={isSelected ? handleDragLeave : undefined}
                    onDrop={isSelected ? (e) => handleDrop(e, img.signed_id) : undefined}
                    onClick={() => toggle(img.signed_id)}
                    className={`group relative cursor-pointer overflow-hidden rounded-lg border-2 transition-all ${
                      isDragOver
                        ? "border-red scale-[1.02] ring-2 ring-red/30"
                        : isSelected
                          ? "border-red ring-1 ring-red/30"
                          : "border-stone/30 opacity-50 hover:opacity-80"
                    } ${isSelected ? "cursor-grab active:cursor-grabbing" : ""}`}
                  >
                    <OptimizedImage
                      src={img.thumb_url}
                      alt=""
                      className="pointer-events-none aspect-[4/3] w-full object-cover"
                    />
                    {isSelected && (
                      <>
                        <div className="absolute left-2 top-2 flex size-6 items-center justify-center rounded-full bg-red text-[11px] font-bold text-white">
                          {orderIndex + 1}
                        </div>
                      </>
                    )}
                  </div>
                  {img.description && (
                    <p className="mt-1 px-0.5 text-[10px] leading-snug text-warm-grey line-clamp-2">
                      {img.description}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
